/* eslint-disable indent */
import checkSvg from '@src/main/assets/icons/check-red.svg';
import { Category } from '@src/main/modules/parameters/domain/issues/category.interface';
import {
  toCreateCategoryRequest,
  toUpdateCategoryRequest,
} from '@src/main/modules/parameters/infrastructure/dto/createUpdateCategoryRequest';
import { getCategorySchema } from '@src/main/modules/parameters/ui/issues/categorySchema';
import CategoryForm from '@src/main/modules/parameters/ui/issues/components/CategoryForm';
import { useCreateCategory } from '@src/main/modules/parameters/ui/issues/hooks/create/useCreateCategory';
import { useDeleteCategory } from '@src/main/modules/parameters/ui/issues/hooks/delete/useDeleteCategory';
import { useCategories } from '@src/main/modules/parameters/ui/issues/hooks/find/useCategories';
import { useUpdateCategory } from '@src/main/modules/parameters/ui/issues/hooks/update/useUpdateCategory';
import { useToast } from '@src/main/shared/context/ToastContext';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTableSingle from '@src/main/shared/ui/components/table/AppTableSingle';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

interface TableRow extends Category {
  id: string;
}

interface CategoryFormState extends Partial<Category> {
  typeTitle?: string;
  typeVisibleOnApp?: boolean;
}

interface CategoriesProps {
  selectedCategory: Category | null;
  onSelectCategory: (category: Category | null) => void;
  resetType: () => void;
}

const Categories: FC<CategoriesProps> = ({ selectedCategory, onSelectCategory, resetType }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: categories = [], isLoading: loadingCategories, repository } = useCategories();
  const { mutateAsync: createCategory } = useCreateCategory();
  const { mutateAsync: updateCategory } = useUpdateCategory();
  const { mutateAsync: deleteCategory } = useDeleteCategory();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditCategory, setNewEditCategory] = useState<CategoryFormState>({
    title: '',
    description: '',
    visibleOnApp: true,
    typeTitle: '',
    typeVisibleOnApp: true,
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const toolbar = [true, true, false, true, false, false];

  const categoriesWithId: TableRow[] = categories.map((category, index) => ({
    id: category.categoryId ?? `temp-id-${index}`,
    ...category,
    visibleOnAppDisplay: category.visibleOnApp ? (
      <img src={checkSvg} alt="Visible" width={16} height={16} />
    ) : null,
  }));

  const handleShowCreateModal = () => {
    setNewEditCategory({
      title: '',
      description: '',
      visibleOnApp: false,
      typeTitle: '',
      typeVisibleOnApp: false,
    });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleEditCategory = (row: TableRow) => {
    setNewEditCategory({
      categoryId: row.categoryId,
      title: row.title,
      description: row.description,
      visibleOnApp: row.visibleOnApp,
    });
    setValidationErrors({});
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    if (!repository) return;

    const isCreate = !newEditCategory.categoryId;
    const schema = getCategorySchema(t, isCreate);

    try {
      schema.parse(newEditCategory);

      if (isCreate) {
        await createCategory(
          toCreateCategoryRequest(
            newEditCategory as Category,
            newEditCategory.typeTitle!,
            newEditCategory.typeVisibleOnApp!,
          ),
        );
        showSuccessMessage(toast, t('parameters.issues.categories.createdSuccess'));
      } else {
        await updateCategory({
          id: newEditCategory.categoryId!,
          data: toUpdateCategoryRequest(newEditCategory as Category),
        });
        showSuccessMessage(toast, t('parameters.issues.categories.updatedSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['categories'] });
      setIsModalVisible(false);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            errors[err.path[0] as string] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handleSelectCategory = (row: TableRow | null) => {
    const cat = row ? categoriesWithId.find((r) => r.id === row.id) || null : null;
    onSelectCategory(cat);
    resetType();
  };

  const handleDeleteClick = (row: TableRow) => {
    setCategoryToDelete({
      categoryId: row.categoryId,
      title: row.title,
      description: row.description,
      visibleOnApp: row.visibleOnApp,
      types: row.types,
    });
  };

  const confirmDelete = async () => {
    if (!repository || !categoryToDelete) return;

    try {
      await deleteCategory(categoryToDelete.categoryId!);

      if (selectedCategory?.categoryId === categoryToDelete.categoryId) {
        onSelectCategory(null);
        resetType();
      }

      await queryClient.invalidateQueries({ queryKey: ['categories'] });
      showSuccessMessage(toast, t('categories.messages.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setCategoryToDelete(null);
    }
  };

  const rejectDelete = () => {
    setCategoryToDelete(null);
  };

  return (
    <>
      <div>
        <div>
          <HeaderTitle title={t('parameters.issues.categories.title')} />
          <TableHeader
            onCreate={handleShowCreateModal}
            onRefresh={async () => {
              setIsRefreshing(true);
              await queryClient.invalidateQueries({ queryKey: ['categories'] });
              setIsRefreshing(false);
            }}
            visibility={toolbar}
          />
        </div>

        <AppLoading isLoading={loadingCategories || isRefreshing}>
          <AppTableSingle
            headers={[
              { name: t('parameters.issues.titleField'), field: 'title' },
              {
                name: t('parameters.issues.visibleOnApp'),
                field: 'visibleOnAppDisplay',
                width: '200px',
              },
            ]}
            data={categoriesWithId}
            onEdit={handleEditCategory}
            onDelete={handleDeleteClick}
            onSelectedChange={handleSelectCategory}
            selectedRow={
              selectedCategory ? { id: selectedCategory.categoryId!, ...selectedCategory } : null
            }
          />
        </AppLoading>
      </div>

      <AppDialog
        visible={isModalVisible}
        title={
          newEditCategory.categoryId
            ? t('parameters.issues.categories.updateCategory', { category: newEditCategory.title })
            : t('parameters.issues.categories.createCategory')
        }
        onClose={() => {
          setIsModalVisible(false);
          setValidationErrors({});
        }}
        disableButtons={loadingCategories}
        onConfirm={handleCreateEdit}
      >
        <CategoryForm
          values={newEditCategory}
          handleChange={(field, value) =>
            setNewEditCategory((prev) => ({ ...prev, [field]: value }))
          }
          errors={validationErrors}
          isEdit={!!newEditCategory.categoryId}
        />
      </AppDialog>

      <ConfirmDeleteDialog
        visible={!!categoryToDelete}
        confirmDelete={confirmDelete}
        rejectDelete={rejectDelete}
        message={t('parameters.issues.categories.confirmDelete', {
          category: categoryToDelete?.title,
        })}
      />
    </>
  );
};

export default Categories;
